import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import {
  HelpCircle, Search, BookOpen, Users, GraduationCap, Shield, Ticket,
  Briefcase, ArrowRight, TrendingUp, ThumbsUp, ThumbsDown, Sparkles, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
// Phase 220.b — Contextual walkthrough player for /help (renders only videos
// the Super Admin tagged with placement='help_center' for the active role)
import SelfServiceWalkthroughSection from '@/components/dashboard/SelfServiceWalkthroughSection';

const catIcons = {
  'Getting Started': BookOpen,
  'Student Portal': GraduationCap,
  'Coach Tools': Briefcase,
  'Academy Management': Users,
  'Account & Security': Shield,
};

const catColors = {
  'Getting Started': 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
  'Student Portal': 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
  'Coach Tools': 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30',
  'Academy Management': 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
  'Account & Security': 'text-slate-600 bg-slate-100 dark:bg-slate-900/30',
};

// Phase 78 — Curated Quick Action tiles by role
const QUICK_ACTIONS_BY_ROLE = {
  academy_owner: [
    { label: 'Add a student', search: 'add student', color: 'from-emerald-500 to-teal-600' },
    { label: 'Run payroll', search: 'payroll',       color: 'from-violet-500 to-fuchsia-600' },
    { label: 'Connect Razorpay', search: 'razorpay', color: 'from-blue-500 to-indigo-600' },
    { label: 'Generate fees',    search: 'fees',     color: 'from-amber-500 to-orange-600' },
    { label: 'Create event',     search: 'event',    color: 'from-rose-500 to-pink-600' },
    { label: 'Send broadcast',   search: 'broadcast', color: 'from-cyan-500 to-sky-600' },
  ],
  super_admin: [
    { label: 'Verify academy',     search: 'verify',    color: 'from-emerald-500 to-teal-600' },
    { label: 'Geo broadcast',      search: 'broadcast', color: 'from-blue-500 to-indigo-600' },
    { label: 'Portfolio forecast', search: 'portfolio', color: 'from-violet-500 to-fuchsia-600' },
    { label: 'Invoices',           search: 'invoice',   color: 'from-amber-500 to-orange-600' },
  ],
  student: [
    { label: 'Pay my fees',         search: 'pay fees',  color: 'from-emerald-500 to-teal-600' },
    { label: 'View progress',       search: 'progress',  color: 'from-violet-500 to-fuchsia-600' },
    { label: 'RSVP for an event',   search: 'event',     color: 'from-blue-500 to-indigo-600' },
    { label: 'Reset my password',   search: 'password',  color: 'from-slate-500 to-slate-700' },
  ],
  parent: [
    { label: 'Pay child fees',      search: 'pay fees',  color: 'from-emerald-500 to-teal-600' },
    { label: "View child's progress", search: 'progress', color: 'from-violet-500 to-fuchsia-600' },
    { label: 'RSVP for event',      search: 'event',     color: 'from-blue-500 to-indigo-600' },
    { label: 'Update contact info', search: 'update',    color: 'from-amber-500 to-orange-600' },
  ],
  coach: [
    { label: 'Mark attendance',    search: 'attendance', color: 'from-emerald-500 to-teal-600' },
    { label: 'Create progress card', search: 'progress',  color: 'from-violet-500 to-fuchsia-600' },
    { label: 'Schedule training',  search: 'training',   color: 'from-blue-500 to-indigo-600' },
  ],
  employee: [
    { label: 'View my payslip',  search: 'payslip',   color: 'from-emerald-500 to-teal-600' },
    { label: 'Apply for leave',  search: 'leave',     color: 'from-blue-500 to-indigo-600' },
    { label: 'Update profile',   search: 'profile',   color: 'from-amber-500 to-orange-600' },
  ],
};

export default function HelpCenter() {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [feedbackGiven, setFeedbackGiven] = useState({}); // faq_id -> 'helpful' | 'not_helpful'
  const [openFaqs, setOpenFaqs] = useState(new Set()); // track opened FAQ ids so view fires once
  const searchRef = useRef(null);

  useEffect(() => {
    const role = user?.role || 'all';
    Promise.all([
      api.get('/faqs', { params: { role } }).catch(() => ({ data: { faqs: [], categories: [] } })),
      api.get('/faqs/popular', { params: { role, limit: 5 } }).catch(() => ({ data: { faqs: [] } })),
    ]).then(([f, p]) => {
      setFaqs(f.data.faqs || []);
      setCategories(f.data.categories || []);
      setPopular(p.data.faqs || []);
    }).finally(() => setLoading(false));
  }, [user]);

  // Keyboard shortcut "/" focuses the search bar
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const quickActions = QUICK_ACTIONS_BY_ROLE[user?.role] || QUICK_ACTIONS_BY_ROLE.academy_owner;

  const filtered = useMemo(() => {
    let result = faqs;
    if (activeCategory !== 'all') result = result.filter(f => f.category === activeCategory);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q) || (f.tags || '').toLowerCase().includes(q));
    }
    return result;
  }, [faqs, activeCategory, search]);

  const groupedFaqs = useMemo(() => {
    const groups = {};
    filtered.forEach(f => {
      if (!groups[f.category]) groups[f.category] = [];
      groups[f.category].push(f);
    });
    return groups;
  }, [filtered]);

  const handleFaqOpened = (faq_id) => {
    if (!faq_id || openFaqs.has(faq_id)) return;
    setOpenFaqs(prev => new Set(prev).add(faq_id));
    api.post(`/faqs/${faq_id}/view`).catch(() => {});
  };

  const handleFeedback = async (faq_id, helpful) => {
    if (feedbackGiven[faq_id]) return;
    setFeedbackGiven(prev => ({ ...prev, [faq_id]: helpful ? 'helpful' : 'not_helpful' }));
    try {
      await api.post(`/faqs/${faq_id}/feedback`, { helpful });
      toast.success(helpful ? 'Thanks for letting us know!' : "Got it — we'll improve this answer.");
    } catch {}
  };

  if (loading) return (
    <div className="space-y-4" data-testid="help-loading">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-10 w-full max-w-md" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="help-center-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-primary" /> Help Center
        </h1>
        <p className="text-sm text-muted-foreground">Find answers to common questions about Out-Play</p>
      </div>

      {/* Phase 220.b — Video walkthroughs tagged for the Help Center placement */}
      <SelfServiceWalkthroughSection placement="help_center" />

      {/* Search with keyboard shortcut */}
      <div className="relative max-w-lg">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={searchRef}
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search FAQs... e.g. attendance, fees, login"
          className="pl-10 pr-20 h-10"
          data-testid="help-search"
        />
        {search ? (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center" data-testid="help-search-clear">
            <X className="h-3 w-3" />
          </button>
        ) : (
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 items-center rounded border bg-muted px-1.5 text-[10px] font-mono text-muted-foreground">/</kbd>
        )}
        {search && <span className="text-[11px] text-muted-foreground mt-1 block ml-1">{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>}
      </div>

      {/* Phase 78 — Quick Action Tiles (role-aware) */}
      {!search && activeCategory === 'all' && (
        <div data-testid="help-quick-actions">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Quick Actions
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {quickActions.map((q, i) => (
              <button
                key={i}
                onClick={() => setSearch(q.search)}
                className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${q.color} text-white p-3 text-left hover:scale-[1.02] transition-transform`}
                data-testid={`help-quick-${q.search.replace(/\s+/g, '-')}`}
              >
                <p className="text-sm font-semibold leading-tight">{q.label}</p>
                <ArrowRight className="h-3.5 w-3.5 mt-1.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phase 78 — Trending FAQs */}
      {!search && activeCategory === 'all' && popular.length > 0 && (
        <Card data-testid="help-trending">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-amber-600" /> Trending
              <Badge variant="outline" className="text-[10px] ml-1">{popular.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1.5">
            {popular.slice(0, 5).map(f => (
              <button
                key={f.id}
                onClick={() => setSearch(f.question.slice(0, 30))}
                className="w-full text-left p-2.5 rounded-lg hover:bg-muted/60 flex items-start gap-2 group"
                data-testid={`help-trending-${f.id}`}
              >
                <TrendingUp className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium group-hover:text-primary truncate">{f.question}</p>
                  {f.views > 0 && <p className="text-[10px] text-muted-foreground">{f.views} view{f.views === 1 ? '' : 's'}</p>}
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={activeCategory === 'all' ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setActiveCategory('all')}>
          All ({faqs.length})
        </Button>
        {categories.map(cat => {
          const Icon = catIcons[cat] || HelpCircle;
          const count = faqs.filter(f => f.category === cat).length;
          return (
            <Button key={cat} size="sm" variant={activeCategory === cat ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setActiveCategory(cat)}>
              <Icon className="h-3.5 w-3.5 mr-1" /> {cat} ({count})
            </Button>
          );
        })}
      </div>

      {/* FAQ Groups */}
      {Object.keys(groupedFaqs).length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Search className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No results found</h3>
            <p className="text-sm text-muted-foreground mb-4">Try different keywords or browse categories</p>
            <Button variant="outline" size="sm" onClick={() => { setSearch(''); setActiveCategory('all'); }}>Clear Filters</Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedFaqs).map(([category, items]) => {
          const Icon = catIcons[category] || HelpCircle;
          const color = catColors[category] || 'text-slate-600 bg-slate-100';
          return (
            <Card key={category}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  {category}
                  <Badge variant="secondary" className="text-[10px] ml-1">{items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Accordion
                  type="single"
                  collapsible
                  className="space-y-1"
                  onValueChange={(val) => {
                    if (!val) return;
                    const id = val.replace(/^faq-/, '');
                    handleFaqOpened(id);
                  }}
                >
                  {items.map((faq, i) => {
                    const fb = feedbackGiven[faq.id];
                    return (
                      <AccordionItem key={faq.id || i} value={`faq-${faq.id || i}`} className="border rounded-lg px-3" data-testid={`help-faq-${faq.id}`}>
                        <AccordionTrigger className="text-left py-3 hover:no-underline text-sm font-medium">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="flex-1 truncate">{faq.question}</span>
                            {(faq.views || 0) > 5 && (
                              <Badge variant="outline" className="text-[9px] ml-2 shrink-0 border-amber-300 text-amber-700 bg-amber-50">
                                <TrendingUp className="h-2.5 w-2.5 mr-0.5" /> Popular
                              </Badge>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3 text-sm text-muted-foreground leading-relaxed">
                          <p>{faq.answer}</p>
                          {/* Feedback widget */}
                          <div className="flex items-center justify-between mt-3 pt-2 border-t">
                            <p className="text-[11px] text-muted-foreground">Was this helpful?</p>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant={fb === 'helpful' ? 'default' : 'outline'}
                                disabled={!!fb}
                                className={`h-6 px-2 text-[11px] ${fb === 'helpful' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                                onClick={() => handleFeedback(faq.id, true)}
                                data-testid={`faq-helpful-${faq.id}`}
                              >
                                <ThumbsUp className="h-3 w-3 mr-1" /> Yes
                                {(faq.helpful_count || 0) > 0 && <span className="ml-1 opacity-70">{faq.helpful_count}</span>}
                              </Button>
                              <Button
                                size="sm"
                                variant={fb === 'not_helpful' ? 'default' : 'outline'}
                                disabled={!!fb}
                                className={`h-6 px-2 text-[11px] ${fb === 'not_helpful' ? 'bg-slate-500 hover:bg-slate-600' : ''}`}
                                onClick={() => handleFeedback(faq.id, false)}
                                data-testid={`faq-not-helpful-${faq.id}`}
                              >
                                <ThumbsDown className="h-3 w-3 mr-1" /> No
                              </Button>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Support CTA */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Ticket className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Didn't find what you need?</p>
              <p className="text-xs text-muted-foreground">Open a support ticket and our team will respond within 24 hours</p>
            </div>
          </div>
          <Link to="/tickets">
            <Button size="sm" variant="outline">Open a Ticket <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
